/**
 * `createApiKeysSource` — the caller's own API keys, over the shared `ApiClient`.
 *
 * Speaks canonical `/api-keys` paths; `RESOURCE_REWRITES` sends them to
 * management-api's `.management.` zone, so region routing, auth and envelope
 * unwrap are inherited.
 *
 * **This adapter never names a tenancy.** The three routes accept a
 * `customer_id`/`org_id`/`tenant_id`/`user_id` target, and the backend refuses it
 * from anyone but an internal admin-portal operator — so a dashboard caller
 * simply sends none and gets their own. A key minted here is stamped with the
 * caller's `customer → org → tenant → user` and carries a SUBSET of the scopes
 * they hold, which is the whole security story: a key can never do more than the
 * person who made it, and it stops working when they lose the access behind it.
 *
 * What comes back is narrower than the tenant's keys. `management_shared.api_keys
 * .may_act_on` gives a customer-facing caller their own keys plus the workspace's
 * shared ones (service keys and keys with no human subject); a colleague's
 * personal key is not the tenant's business. Holding `api-keys:delete` widens
 * that to everything in the tenant, which is how a workspace admin cleans up
 * after someone who has left.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { asNullableString, asRecord, asString, rowsFromWire } from "./wire";

/** The path every call here rewrites from. */
const PATH = "/api-keys";

/**
 * Where a key sits on the tenancy ladder, as the backend reports it.
 *
 * The dashboard only ever MINTS at the `user` rung — its caller has a subject —
 * but it can SEE the wider rungs, because a key minted for the whole tenant (by
 * an admin-portal operator supporting this customer) is one this workspace has
 * to be able to find and revoke.
 */
export type KeyRung = "customer" | "org" | "tenant" | "user";

export interface ApiKeySummary {
  id: string;
  /** `ake_<id>…<last4>` — all that survives of the secret. */
  masked: string;
  name: string;
  description: string | null;
  rung: KeyRung;
  /** The human it belongs to, a synthetic `svc:…` subject, or null. */
  userId: string | null;
  isService: boolean;
  scopes: string[];
  teamIds: string[];
  status: string;
  createdAt: string | null;
  createdBy: string | null;
  /** Epoch seconds, or null for a key that never expires. */
  expiresAt: number | null;
  lastUsedAt: number | null;
  revokedAt: number | null;
}

/** The create response — the one and only time the plaintext exists. */
export interface MintedApiKey extends ApiKeySummary {
  secret: string;
}

export interface MintApiKeyInput {
  name: string;
  description?: string;
  /** Must be a subset of the caller's own scopes; the backend re-checks. */
  scopes: string[];
  /** Days until expiry. `null` mints a key that never expires. */
  expiresInDays: number | null;
  /** A machine key: synthetic subject, no live team resolution, may pin teams. */
  isService?: boolean;
  teamIds?: string[];
}

export interface ApiKeysSource {
  list(includeRevoked?: boolean): Promise<ApiKeySummary[]>;
  mint(input: MintApiKeyInput): Promise<MintedApiKey>;
  revoke(id: string): Promise<void>;
}

const RUNGS: readonly string[] = ["customer", "org", "tenant", "user"];

function keyFromWire(raw: Record<string, unknown>): ApiKeySummary {
  const rung = asString(raw.rung);
  return {
    id: asString(raw.id ?? raw.api_key_id),
    masked: asString(raw.masked),
    name: asString(raw.name),
    description: asNullableString(raw.description),
    // An unknown rung reads as `user` rather than throwing: the row is real and
    // the page's job is to show it, and `user` is the narrowest claim to make
    // about a key whose reach we could not parse.
    rung: (RUNGS.includes(rung) ? rung : "user") as KeyRung,
    userId: asNullableString(raw.user_id),
    isService: Boolean(raw.is_service),
    scopes: Array.isArray(raw.scopes) ? raw.scopes.map((s) => asString(s)) : [],
    teamIds: Array.isArray(raw.team_ids) ? raw.team_ids.map((t) => asString(t)) : [],
    status: asString(raw.status ?? "active"),
    createdAt: asNullableString(raw.created_at),
    createdBy: asNullableString(raw.created_by),
    expiresAt: typeof raw.expires_at === "number" ? raw.expires_at : null,
    lastUsedAt: typeof raw.last_used_at === "number" ? raw.last_used_at : null,
    revokedAt: typeof raw.revoked_at === "number" ? raw.revoked_at : null,
  };
}

/** The POST body. Only the fields the caller actually set — see the notes. */
export function mintBody(input: MintApiKeyInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: input.name.trim(),
    scopes: input.scopes,
  };
  const description = input.description?.trim();
  if (description) body.description = description;
  // Absent means "never expires"; `0` would be a client error, so the null has
  // to be dropped rather than sent.
  if (input.expiresInDays != null) body.expires_in_days = input.expiresInDays;
  if (input.isService) {
    body.is_service = true;
    // `team_ids` on a USER key is a hard rejection, not an ignored field, so a
    // toggled-off service switch must not leak the list it left behind.
    if (input.teamIds?.length) body.team_ids = input.teamIds;
  }
  return body;
}

export function createApiKeysSource(client: ApiClient): ApiKeysSource {
  return {
    async list(includeRevoked = false): Promise<ApiKeySummary[]> {
      const res = await client.get<unknown>(PATH, {
        query: includeRevoked ? { include_revoked: "true" } : undefined,
        // A key the caller just minted or revoked has to be in the next read;
        // this list is short and changes only when they change it.
        cacheTtlMs: 0,
      });
      return rowsFromWire(unwrapData(res)).map(keyFromWire);
    },

    async mint(input: MintApiKeyInput): Promise<MintedApiKey> {
      // `unwrapData`, not a bare await: `ApiClient` RESOLVES an `{ok:false}`
      // envelope rather than throwing, so a refusal would otherwise be reported
      // as a minted key with an undefined secret.
      const created = asRecord(unwrapData(await client.post<unknown>(PATH, mintBody(input))));
      client.invalidate(PATH);
      return { ...keyFromWire(created), secret: asString(created.api_key) };
    },

    async revoke(id: string): Promise<void> {
      unwrapData(await client.delete<void>(`${PATH}/${encodeURIComponent(id)}`));
      client.invalidate(PATH);
    },
  };
}
